import React from "react";

export interface BillingDraft {
  email: string;
  firstName: string;
  lastName: string;
  company?: string;
  phone?: string;
  line1: string;
  line2?: string;
  city: string;
  state?: string;
  postcode: string;
  country: string; // ISO-3166 alpha-2
}

const COUNTRIES: Array<{ code: string; label: string }> = [
  { code: "US", label: "United States" },
  { code: "GB", label: "United Kingdom" },
  { code: "ZM", label: "Zambia" },
  { code: "ZA", label: "South Africa" },
  { code: "KE", label: "Kenya" },
  { code: "NG", label: "Nigeria" },
  { code: "CA", label: "Canada" },
  { code: "AU", label: "Australia" },
  { code: "DE", label: "Germany" },
  { code: "FR", label: "France" },
  { code: "NL", label: "Netherlands" },
  { code: "IE", label: "Ireland" },
  { code: "OTHER", label: "Other (enter manually)" },
];

interface Props {
  value: BillingDraft;
  onChange: (next: BillingDraft) => void;
  disabled?: boolean;
}

export function BillingAddressFields({ value, onChange, disabled }: Props) {
  function patch(part: Partial<BillingDraft>) {
    onChange({ ...value, ...part });
  }
  function fieldClass() {
    return "w-full bg-paper border border-hairline px-3 py-2 font-mono text-sm text-ink focus:outline-none focus:border-ink";
  }
  function labelClass() {
    return "block font-mono text-[10px] text-ink-3 uppercase tracking-widest mb-1";
  }
  return (
    <div className="space-y-4">
      <div>
        <label className={labelClass()} htmlFor="b-email">Email</label>
        <input
          id="b-email"
          type="email"
          autoComplete="email"
          required
          disabled={disabled}
          className={fieldClass()}
          value={value.email}
          onChange={(e) => patch({ email: e.target.value })}
        />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className={labelClass()} htmlFor="b-first">First name</label>
          <input
            id="b-first"
            autoComplete="given-name"
            required
            disabled={disabled}
            className={fieldClass()}
            value={value.firstName}
            onChange={(e) => patch({ firstName: e.target.value })}
          />
        </div>
        <div>
          <label className={labelClass()} htmlFor="b-last">Last name</label>
          <input
            id="b-last"
            autoComplete="family-name"
            required
            disabled={disabled}
            className={fieldClass()}
            value={value.lastName}
            onChange={(e) => patch({ lastName: e.target.value })}
          />
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className={labelClass()} htmlFor="b-company">Company (optional)</label>
          <input
            id="b-company"
            autoComplete="organization"
            disabled={disabled}
            className={fieldClass()}
            value={value.company ?? ""}
            onChange={(e) => patch({ company: e.target.value })}
          />
        </div>
        <div>
          <label className={labelClass()} htmlFor="b-phone">Phone (optional)</label>
          <input
            id="b-phone"
            type="tel"
            autoComplete="tel"
            disabled={disabled}
            className={fieldClass()}
            value={value.phone ?? ""}
            onChange={(e) => patch({ phone: e.target.value })}
          />
        </div>
      </div>
      <div>
        <label className={labelClass()} htmlFor="b-line1">Address line 1</label>
        <input
          id="b-line1"
          autoComplete="address-line1"
          required
          disabled={disabled}
          className={fieldClass()}
          value={value.line1}
          onChange={(e) => patch({ line1: e.target.value })}
        />
      </div>
      <div>
        <label className={labelClass()} htmlFor="b-line2">Address line 2 (optional)</label>
        <input
          id="b-line2"
          autoComplete="address-line2"
          disabled={disabled}
          className={fieldClass()}
          value={value.line2 ?? ""}
          onChange={(e) => patch({ line2: e.target.value })}
        />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className={labelClass()} htmlFor="b-city">City</label>
          <input
            id="b-city"
            autoComplete="address-level2"
            required
            disabled={disabled}
            className={fieldClass()}
            value={value.city}
            onChange={(e) => patch({ city: e.target.value })}
          />
        </div>
        <div>
          <label className={labelClass()} htmlFor="b-state">State / region</label>
          <input
            id="b-state"
            autoComplete="address-level1"
            disabled={disabled}
            className={fieldClass()}
            value={value.state ?? ""}
            onChange={(e) => patch({ state: e.target.value })}
          />
        </div>
        <div>
          <label className={labelClass()} htmlFor="b-postcode">Postal code</label>
          <input
            id="b-postcode"
            autoComplete="postal-code"
            required
            disabled={disabled}
            className={fieldClass()}
            value={value.postcode}
            onChange={(e) => patch({ postcode: e.target.value })}
          />
        </div>
      </div>
      <div>
        <label className={labelClass()} htmlFor="b-country">Country</label>
        <select
          id="b-country"
          required
          disabled={disabled}
          className={fieldClass()}
          value={value.country}
          onChange={(e) => patch({ country: e.target.value })}
        >
          {COUNTRIES.map((c) => (
            <option key={c.code} value={c.code}>
              {c.label}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}

export function emptyBilling(country = "US"): BillingDraft {
  return {
    email: "",
    firstName: "",
    lastName: "",
    company: "",
    phone: "",
    line1: "",
    line2: "",
    city: "",
    state: "",
    postcode: "",
    country,
  };
}
